/**
 * Settings Bridge
 *
 * Centralizes extraction and application of user settings from/to the Zustand store.
 * Used by the settings sync hook to persist UI preferences to the API.
 *
 * Each setting category is JSON-serialized as a string value for API transport.
 * Only syncable fields are included -- transient/session-specific state is excluded.
 */

import { isValidBasemapId } from '../config/maplibre';
import type { BasemapId } from '../config/maplibre';
import type { GeoMarkupStore } from './index';

// ===== HELPERS =====

/** Safely parse a JSON string, returning null on failure */
function tryParseJson(value: string): Record<string, unknown> | null {
  try {
    return JSON.parse(value) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Normalize a persisted filter value (legacy scalar/null or array) to an array. */
function normalizeToArray(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined) return [];
  return [value];
}

// ===== TYPES =====

/** Setting keys that are synced to the API */
export const SYNCED_SETTING_KEYS = [
  'filters',
  'note_filters',
  'ui',
  'search',
  'guides',
  'map',
  'basemap_by_workspace',
] as const;

export type SyncedSettingKey = (typeof SYNCED_SETTING_KEYS)[number];

// ===== EXTRACT =====

/**
 * Extract syncable settings from store state.
 * Each category is JSON-serialized as a string value.
 */
export function extractSettings(
  state: GeoMarkupStore
): Record<SyncedSettingKey, string> {
  return {
    filters: JSON.stringify({
      statusFilter: state.taskFilters.status,
      priorityFilter: state.taskFilters.priority,
      assignedToIdFilter: state.taskFilters.assignedToId,
      locationFilter: state.taskFilters.location,
    }),
    note_filters: JSON.stringify({
      visibility: state.noteFilters.visibility,
      colors: state.noteFilters.colors,
      sortBy: state.noteFilters.sortBy,
      sortDescending: state.noteFilters.sortDescending,
    }),
    ui: JSON.stringify({
      taskViewMode: state.ui.taskViewMode,
      noteViewMode: state.ui.noteViewMode,
      hasSeenGuide: state.ui.toolbarGuide.hasSeenGuide,
      hasSeenScrollHint: state.ui.toolbarScrollHint.hasSeenScrollHint,
      swipeDownHintShown: state.ui.slidePanel.swipeDownHintShown,
      swipeUpHintShown: state.ui.slidePanel.swipeUpHintShown,
      dashboardDateRange: state.ui.dashboard.dateRange,
    }),
    search: JSON.stringify({
      selectedPlaceTypes: Array.from(state.search.selectedPlaceTypes).sort(),
      useLocationPriority: state.search.useLocationPriority,
    }),
    guides: JSON.stringify({
      dismissedGuides: state.guides.dismissedGuides,
    }),
    map: JSON.stringify({
      is3DMode: state.map.is3DMode,
      shareLocation: state.map.shareLocation,
    }),
    basemap_by_workspace: JSON.stringify(state.map.userBasemapByWorkspace),
  };
}

// ===== APPLY =====

/**
 * Apply settings from API response to store state.
 * Uses Immer-compatible setState from Zustand.
 */
export function applySettings(
  settings: Record<string, string>,
  setState: (fn: (state: GeoMarkupStore) => void) => void
): void {
  setState((state) => {
    const filtersData = settings.filters
      ? tryParseJson(settings.filters)
      : null;
    if (filtersData) {
      if (filtersData.statusFilter !== undefined)
        state.taskFilters.status = normalizeToArray(
          filtersData.statusFilter
        ) as typeof state.taskFilters.status;
      if (filtersData.priorityFilter !== undefined)
        state.taskFilters.priority = normalizeToArray(
          filtersData.priorityFilter
        ) as typeof state.taskFilters.priority;
      if (filtersData.assignedToIdFilter !== undefined)
        state.taskFilters.assignedToId = normalizeToArray(
          filtersData.assignedToIdFilter
        ) as typeof state.taskFilters.assignedToId;
      if (filtersData.locationFilter !== undefined)
        state.taskFilters.location =
          filtersData.locationFilter as typeof state.taskFilters.location;
    }

    const noteFiltersData = settings.note_filters
      ? tryParseJson(settings.note_filters)
      : null;
    if (noteFiltersData) {
      if (noteFiltersData.visibility !== undefined)
        state.noteFilters.visibility =
          noteFiltersData.visibility as typeof state.noteFilters.visibility;
      if (noteFiltersData.colors !== undefined)
        state.noteFilters.colors =
          noteFiltersData.colors as typeof state.noteFilters.colors;
      if (noteFiltersData.sortBy !== undefined)
        state.noteFilters.sortBy =
          noteFiltersData.sortBy as typeof state.noteFilters.sortBy;
      if (noteFiltersData.sortDescending !== undefined)
        state.noteFilters.sortDescending =
          noteFiltersData.sortDescending as typeof state.noteFilters.sortDescending;
    }

    const uiData = settings.ui ? tryParseJson(settings.ui) : null;
    if (uiData) {
      if (uiData.taskViewMode !== undefined)
        state.ui.taskViewMode =
          uiData.taskViewMode as typeof state.ui.taskViewMode;
      if (uiData.noteViewMode !== undefined)
        state.ui.noteViewMode =
          uiData.noteViewMode as typeof state.ui.noteViewMode;
      if (uiData.hasSeenGuide !== undefined)
        state.ui.toolbarGuide.hasSeenGuide = uiData.hasSeenGuide as boolean;
      if (uiData.hasSeenScrollHint !== undefined)
        state.ui.toolbarScrollHint.hasSeenScrollHint =
          uiData.hasSeenScrollHint as boolean;
      if (uiData.swipeDownHintShown !== undefined)
        state.ui.slidePanel.swipeDownHintShown =
          uiData.swipeDownHintShown as boolean;
      if (uiData.swipeUpHintShown !== undefined)
        state.ui.slidePanel.swipeUpHintShown =
          uiData.swipeUpHintShown as boolean;
      if (uiData.dashboardDateRange !== undefined)
        state.ui.dashboard.dateRange =
          uiData.dashboardDateRange as typeof state.ui.dashboard.dateRange;
    }

    const searchData = settings.search ? tryParseJson(settings.search) : null;
    if (searchData) {
      if (
        searchData.selectedPlaceTypes !== undefined &&
        Array.isArray(searchData.selectedPlaceTypes)
      ) {
        state.search.selectedPlaceTypes = new Set(
          searchData.selectedPlaceTypes
        ) as typeof state.search.selectedPlaceTypes;
      }
      if (searchData.useLocationPriority !== undefined)
        state.search.useLocationPriority =
          searchData.useLocationPriority as boolean;
    }

    const guidesData = settings.guides ? tryParseJson(settings.guides) : null;
    if (guidesData) {
      if (
        guidesData.dismissedGuides !== undefined &&
        Array.isArray(guidesData.dismissedGuides)
      ) {
        state.guides.dismissedGuides = guidesData.dismissedGuides as string[];
      }
    }

    const mapData = settings.map ? tryParseJson(settings.map) : null;
    if (mapData) {
      if (mapData.is3DMode !== undefined)
        state.map.is3DMode = mapData.is3DMode as boolean;
      if (mapData.shareLocation !== undefined)
        state.map.shareLocation = mapData.shareLocation as boolean;
    }

    const basemapByWorkspaceData = settings.basemap_by_workspace
      ? tryParseJson(settings.basemap_by_workspace)
      : null;
    if (basemapByWorkspaceData && !Array.isArray(basemapByWorkspaceData)) {
      const validated: Record<string, BasemapId> = {};
      for (const [workspaceId, basemapId] of Object.entries(
        basemapByWorkspaceData
      )) {
        if (typeof basemapId === 'string' && isValidBasemapId(basemapId)) {
          validated[workspaceId] = basemapId;
        }
      }
      state.map.userBasemapByWorkspace = validated;
    }
  });
}
